import { tokens } from "@blurb/codex-react";
import { createStyles, Drawer, DrawerProps, Loader, useMantineTheme, Text } from "@mantine/core";
import { isGraphQLError } from "@rpi/openapi-api";
import { Flex, IconV2, RpiText } from "@rpi/openapi-core";
import React from "react";
import { createPortal } from "react-dom";
import { useDevMode } from "../../hooks/useDevMode";
import { optObj } from "../../utils/misc.util";
import { RpaButtonLink } from "../../components/RpiLink";
import { IconV3 } from "@rpi/openapi-core";

export interface RpaDrawerProps extends Omit<DrawerProps, "classNames" | "id"> {
  isLoading?: boolean;
  error?: any;
  overlay?: Omit<RpaDrawerOverlayProps, "overlay">;
}

type RpaDrawerContextType = {
  footerContainer: HTMLDivElement | null;
  headerContentContainer: HTMLDivElement | null;
};

const RpaDrawerContext = React.createContext<RpaDrawerContextType>({
  footerContainer: null,
  headerContentContainer: null,
});

const useRpaDrawerStyles = createStyles((theme) => {
  return {
    overlay: {
      opacity: `1 !important`,
      backgroundColor: "rgba(92, 92, 92, 0.67) !important",
      flex: 1,
      overflowY: "auto",
      gap: `${theme.spacing.md}px !important`,
    },
    drawer: {
      width: 362,
      padding: `0px`,
      display: "flex",
      flexDirection: "column",
    },
    header: {
      alignItems: "flex-start",
      padding: `${theme.spacing.lg}px`,
      paddingBottom: tokens.spacing[6],
      width: "100%",
      margin: 0,
      borderBottom: `1px solid ${tokens.color.semantic.border.faint}`,
    },
    title: {
      fontWeight: theme.other.fontWeights.semibold,
      fontSize: theme.other.fontTypes.h3.fontSize,
      lineHeight: theme.other.fontTypes.h3.lineHeight,
      marginRight: 0,
      width: "100%",
      textAlign: "left",
      flex: 1,
    },
    body: {
      flex: 1,
      overflowY: "auto",
      gap: tokens.spacing[2],
      padding: `${tokens.spacing[6]} 0`,
    },
    footer: {
      flex: "0 0 auto",
      borderTop: `1px solid ${tokens.color.semantic.border.faint}`,
      "&:empty": {
        display: "none",
      },
    },
    portalTarget: {
      "&:empty": {
        display: "none",
      },
    },
  };
});

export function RpaDrawer({ title, onClose, isLoading, error, overlay, children, ...props }: RpaDrawerProps) {
  const devMode = useDevMode();
  const { classes } = useRpaDrawerStyles();
  const theme = useMantineTheme();
  const [footerContainer, setFooterContainer] = React.useState<HTMLDivElement | null>(null);
  const [headerContentContainer, setHeaderContentContainer] = React.useState<HTMLDivElement | null>(null);

  const childrenRender = React.useMemo(() => {
    if (overlay) return;

    if (error) {
      return (
        <RpaDrawerOverlay
          overlay={false}
          type="error"
          message={isGraphQLError(error) ? error.message : "Failed to load."}
        />
      );
    }

    if (isLoading) {
      return <RpaDrawerOverlay overlay={false} type="loading" />;
    }

    if (devMode.isDevMode) return <devMode.DevPreview fromChildren={children} />;

    return children;
  }, [isLoading, error, overlay, children, devMode]);

  return (
    <RpaDrawerContext.Provider value={{ footerContainer, headerContentContainer }}>
      <Drawer
        classNames={classes}
        position="right"
        withCloseButton={false}
        onClose={onClose}
        title={
          <Flex.Column fullWidth gap={tokens.spacing[2]}>
            <Flex.Row fullWidth justify="space-between" align="flex-start">
              <Text>{title || ""}</Text>
              <RpaButtonLink onClick={onClose}>
                <IconV3 icon="close" color={theme.other.semanticColors.textIconBold} />
              </RpaButtonLink>
            </Flex.Row>
            <div ref={setHeaderContentContainer} className={classes.portalTarget} />
          </Flex.Column>
        }
        {...props}
      >
        {!overlay && (
          <>
            <Flex.Column className={classes.body} children={childrenRender} />
            <div ref={setFooterContainer} className={classes.footer} />
          </>
        )}
        {overlay && <RpaDrawerOverlay overlay={true} {...overlay} />}
      </Drawer>
    </RpaDrawerContext.Provider>
  );
}

const RpaDrawerHeaderContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { headerContentContainer } = React.useContext(RpaDrawerContext);
  if (!headerContentContainer) return null;
  return createPortal(children, headerContentContainer);
};

const RpaDrawerFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { footerContainer } = React.useContext(RpaDrawerContext);
  if (!footerContainer) return null;
  return createPortal(children, footerContainer);
};

RpaDrawer.HeaderContent = RpaDrawerHeaderContent;
RpaDrawer.Footer = RpaDrawerFooter;

export interface RpaDrawerOverlayProps {
  type: "loading" | "success" | "error";
  message?: string;
  description?: string;
  overlay?: boolean;
}

export const RpaDrawerOverlay: React.FC<RpaDrawerOverlayProps> = ({ type, message, description, overlay = true }) => {
  const { colors } = useMantineTheme();

  const icon = React.useMemo(() => {
    switch (type) {
      case "loading":
        return <Loader color={colors.calm[5]} size={100} />;
      case "success":
        return <IconV2.Success color={colors.calm[5]} size={{ height: 100 }} />;
      case "error":
        return <IconV2.ErrorOutline color={colors.spark[5]} size={{ height: 100 }} />;
    }
  }, [type]);

  return (
    <Flex.Column
      justify="center"
      align="center"
      gap={8}
      px={16}
      py={32}
      sx={[
        (theme) => ({
          background: theme.colors.sky[5],
          width: "100%",
          height: "100%",
        }),
        optObj(overlay, {
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 100,
        }),
      ]}
    >
      <Flex mb={16} children={icon} />

      {message && (
        <RpiText
          type="p2"
          weight="regular"
          color={(theme) => theme.colors.brand[5]}
          sx={{ textAlign: "center" }}
          children={message}
        />
      )}

      {description && (
        <RpiText
          type="error"
          weight="regular"
          color={(theme) => theme.colors.brand[4]}
          sx={{ textAlign: "center" }}
          children={description}
        />
      )}
    </Flex.Column>
  );
};
